/**
 * IndexedDB Storage Service for Bonndle PWA
 * Provides offline data persistence and sync queue
 */

import { openDB } from 'idb';

const DB_NAME = 'bonndle-pwa';
const DB_VERSION = 1;

// Store names
const STORES = {
  USER: 'user',
  GROUPS: 'groups',
  DEALS: 'deals',
  NOTIFICATIONS: 'notifications',
  FEED: 'feed',
  MESSAGES: 'messages',
  REWARDS: 'rewards',
  PENDING_ACTIONS: 'pendingActions',
};

/**
 * Initialize IndexedDB database
 */
async function initDB() {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion, newVersion, transaction) {
      // User store (single record)
      if (!db.objectStoreNames.contains(STORES.USER)) {
        db.createObjectStore(STORES.USER, { keyPath: 'id' });
      }

      // Groups store
      if (!db.objectStoreNames.contains(STORES.GROUPS)) {
        const groupStore = db.createObjectStore(STORES.GROUPS, { keyPath: 'id' });
        groupStore.createIndex('niche', 'niche');
      }

      // Deals store
      if (!db.objectStoreNames.contains(STORES.DEALS)) {
        const dealStore = db.createObjectStore(STORES.DEALS, { keyPath: 'id' });
        dealStore.createIndex('groupId', 'group_id');
      }

      // Notifications store
      if (!db.objectStoreNames.contains(STORES.NOTIFICATIONS)) {
        const notifStore = db.createObjectStore(STORES.NOTIFICATIONS, { keyPath: 'id' });
        notifStore.createIndex('userId', 'user_id');
        notifStore.createIndex('read', 'read');
      }

      // Feed store
      if (!db.objectStoreNames.contains(STORES.FEED)) {
        const feedStore = db.createObjectStore(STORES.FEED, { keyPath: 'id' });
        feedStore.createIndex('type', 'type');
      }

      // Messages store
      if (!db.objectStoreNames.contains(STORES.MESSAGES)) {
        const msgStore = db.createObjectStore(STORES.MESSAGES, { keyPath: 'id' });
        msgStore.createIndex('groupId', 'group_id');
      }

      // Rewards store
      if (!db.objectStoreNames.contains(STORES.REWARDS)) {
        db.createObjectStore(STORES.REWARDS, { keyPath: 'user_id' });
      }

      // Pending actions queue (for offline sync)
      if (!db.objectStoreNames.contains(STORES.PENDING_ACTIONS)) {
        const pendingStore = db.createObjectStore(STORES.PENDING_ACTIONS, {
          keyPath: 'id',
          autoIncrement: true,
        });
        pendingStore.createIndex('type', 'type');
        pendingStore.createIndex('createdAt', 'createdAt');
      }
    },
  });
}

// Singleton DB instance
let dbInstance = null;

async function getDB() {
  if (!dbInstance) {
    dbInstance = await initDB();
  }
  return dbInstance;
}

/**
 * Storage API
 */
export const storage = {
  // ==================== User ====================

  async getUser() {
    const db = await getDB();
    const users = await db.getAll(STORES.USER);
    return users[0] || null;
  },

  async saveUser(user) {
    const db = await getDB();
    await db.put(STORES.USER, user);
  },

  async clearUser() {
    const db = await getDB();
    await db.clear(STORES.USER);
  },

  // ==================== Groups ====================

  async getGroups() {
    const db = await getDB();
    return db.getAll(STORES.GROUPS);
  },

  async getGroup(id) {
    const db = await getDB();
    return db.get(STORES.GROUPS, id);
  },

  async saveGroups(groups) {
    const db = await getDB();
    const tx = db.transaction(STORES.GROUPS, 'readwrite');
    await Promise.all(groups.map((g) => tx.store.put(g)));
    await tx.done;
  },

  async saveGroup(group) {
    const db = await getDB();
    await db.put(STORES.GROUPS, group);
  },

  // ==================== Deals ====================

  async getDeals(groupId = null) {
    const db = await getDB();
    if (groupId) {
      return db.getAllFromIndex(STORES.DEALS, 'groupId', groupId);
    }
    return db.getAll(STORES.DEALS);
  },

  async getDeal(id) {
    const db = await getDB();
    return db.get(STORES.DEALS, id);
  },

  async saveDeals(deals) {
    const db = await getDB();
    const tx = db.transaction(STORES.DEALS, 'readwrite');
    await Promise.all(deals.map((d) => tx.store.put(d)));
    await tx.done;
  },

  // ==================== Notifications ====================

  async getNotifications(unreadOnly = false) {
    const db = await getDB();
    const all = await db.getAll(STORES.NOTIFICATIONS);

    if (unreadOnly) {
      return all.filter((n) => !n.read);
    }
    return all.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  },

  async saveNotifications(notifications) {
    const db = await getDB();
    const tx = db.transaction(STORES.NOTIFICATIONS, 'readwrite');
    await Promise.all(notifications.map((n) => tx.store.put(n)));
    await tx.done;
  },

  async markNotificationRead(id) {
    const db = await getDB();
    const notif = await db.get(STORES.NOTIFICATIONS, id);
    if (notif) {
      notif.read = true;
      await db.put(STORES.NOTIFICATIONS, notif);
    }
  },

  // ==================== Feed ====================

  async getFeed() {
    const db = await getDB();
    const feed = await db.getAll(STORES.FEED);
    return feed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  },

  async saveFeed(items) {
    const db = await getDB();
    const tx = db.transaction(STORES.FEED, 'readwrite');
    await Promise.all(items.map((i) => tx.store.put(i)));
    await tx.done;
  },

  async updateFeedItem(item) {
    const db = await getDB();
    await db.put(STORES.FEED, item);
  },

  // ==================== Messages ====================

  async getMessages(groupId) {
    const db = await getDB();
    const messages = await db.getAllFromIndex(STORES.MESSAGES, 'groupId', groupId);
    return messages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  },

  async saveMessages(messages) {
    const db = await getDB();
    const tx = db.transaction(STORES.MESSAGES, 'readwrite');
    await Promise.all(messages.map((m) => tx.store.put(m)));
    await tx.done;
  },

  async addMessage(message) {
    const db = await getDB();
    await db.put(STORES.MESSAGES, message);
  },

  // ==================== Rewards ====================

  async getRewards() {
    const db = await getDB();
    const rewards = await db.getAll(STORES.REWARDS);
    return rewards[0] || null;
  },

  async saveRewards(rewards) {
    const db = await getDB();
    await db.put(STORES.REWARDS, rewards);
  },

  // ==================== Pending Actions (Offline Queue) ====================

  async addPendingAction(action) {
    const db = await getDB();
    const pendingAction = {
      ...action,
      createdAt: new Date().toISOString(),
      retryCount: 0,
    };
    return db.add(STORES.PENDING_ACTIONS, pendingAction);
  },

  async getPendingActions() {
    const db = await getDB();
    return db.getAll(STORES.PENDING_ACTIONS);
  },

  async removePendingAction(id) {
    const db = await getDB();
    await db.delete(STORES.PENDING_ACTIONS, id);
  },

  async updatePendingAction(id, updates) {
    const db = await getDB();
    const action = await db.get(STORES.PENDING_ACTIONS, id);
    if (action) {
      await db.put(STORES.PENDING_ACTIONS, { ...action, ...updates });
    }
  },

  async clearPendingActions() {
    const db = await getDB();
    await db.clear(STORES.PENDING_ACTIONS);
  },

  // ==================== Utility ====================

  async clearAll() {
    const db = await getDB();
    await Promise.all(
      Object.values(STORES).map((store) => db.clear(store))
    );
  },

  async getLastSyncTime(key) {
    const stored = localStorage.getItem(`bonndle_sync_${key}`);
    return stored ? new Date(stored) : null;
  },

  async setLastSyncTime(key) {
    localStorage.setItem(`bonndle_sync_${key}`, new Date().toISOString());
  },
};

export default storage;
